import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/utils/subscription';
import { getClinicId, getClinicSession, listClinicDemoProfiles, setClinicId as persistClinicId } from '../api/client';
import { OaAppProvider } from './OaAppContext';
import { oaAppPath } from './paths';
import './oa-app.css';
import '../styles/oa-workspace.css';

const NAV = [
  { to: oaAppPath(), label: 'Home', end: true },
  { to: oaAppPath('/refer'), label: 'Refer' },
  { to: oaAppPath('/inbox'), label: 'Inbox' },
  { to: oaAppPath('/clinic'), label: 'Clinic' },
  { to: oaAppPath('/pathways'), label: 'Pathways' },
  { to: oaAppPath('/billing'), label: 'Plan' },
  { to: oaAppPath('/settings'), label: 'Account' },
];

export function OncoAgentAppShell(): React.ReactElement {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [clinicLabel, setClinicLabel] = useState<string | null>(null);
  const [clinicId, setClinicIdState] = useState<string | null>(null);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    let id = getClinicId();
    const profiles = listClinicDemoProfiles();
    if (!id && getClinicSession()) {
      id = profiles[0]?.clinic_id || null;
      if (id) persistClinicId(id);
    }
    setClinicIdState(id);
    const match = profiles.find((p) => p.clinic_id === id) || profiles[0];
    setClinicLabel(match?.clinic_name || (getClinicSession() ? 'Practice connected' : null));
  }, [loc.pathname]);

  const admin = useMemo(() => isAdmin(user), [user]);

  return (
    <OaAppProvider>
      <div className="oa-app">
        <div
          className={`oa-app-overlay${open ? ' is-open' : ''}`}
          onClick={() => setOpen(false)}
          aria-hidden={!open}
        />
        <aside className={`oa-app-sidebar${open ? ' is-open' : ''}`}>
          <Link to="/oncoagent" className="oa-app-brand">
            <strong>OncoAgent</strong>
            <span>ByOnco</span>
          </Link>
          <nav className="oa-app-nav" aria-label="OncoAgent app">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? 'active' : undefined)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="oa-app-clinic">
            <strong>{user?.email || 'Signed in'}</strong>
            {admin ? <div>Admin access. Full workspace.</div> : <div>Paid clinic seat.</div>}
            {clinicId ? (
              <div style={{ marginTop: 8 }}>
                {clinicLabel || 'Practice connected'}
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>Open Clinic to set up your practice.</div>
            )}
          </div>
        </aside>
        <main className="oa-app-main">
          <div className="oa-app-mobile">
            <button type="button" onClick={() => setOpen(true)} aria-label="Open menu">
              Menu
            </button>
            <strong>OncoAgent</strong>
            <span />
          </div>
          <Suspense fallback={<p style={{ color: '#6d6a63' }}>Loading workspace…</p>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </OaAppProvider>
  );
}

export default OncoAgentAppShell;
